/* eslint-disable react/prop-types */
import React, { useEffect } from 'react';
import { useFieldArray, Controller } from 'react-hook-form';
import { Table, Form, Button, Row, Col } from 'react-bootstrap';
import { MdDelete } from 'react-icons/md';

export const DynamicTableForm = ({
  control,
  errors,
  tableConfig,
  onRowChange
}) => {
  const { fields, append, remove, setValue } = useFieldArray({
    control,
    name: tableConfig.id,
    defaultValue: tableConfig.table.rows
  });

  // Ensure that the first option is selected as the default for new rows
  useEffect(() => {
    if (fields.length === 0) return;
    fields.forEach((row, index) => {
      tableConfig.table.columns.forEach(col => {
        if (col.type === 'dropdown' && !row[col.id]) {
          setValue(
            `${tableConfig.id}[${index}].${col.id}`,
            col.options[0]?.value
          );
        }
      });
    });
  }, [fields, setValue, tableConfig]);

  const handleRowChange = (index, column, value) => {
    const updatedRows = fields.map((row, idx) => {
      if (idx === index) {
        return { ...row, [column]: value };
      }
      return row;
    });

    onRowChange(tableConfig.id, updatedRows);
  };

  const handleAddRow = () => {
    const newRow = {};
    tableConfig.table.columns.forEach(col => {
      newRow[col.id] =
        col.defaultValue ||
        (col.type === 'dropdown' ? col.options[0]?.value : '');
    });
    append(newRow);
  };

  const handleRemoveRow = index => {
    remove(index);
    const updatedRows = fields.filter((_, idx) => idx !== index);
    onRowChange(tableConfig.id, updatedRows);
  };

  const displayRows = fields.length > 0 ? fields : tableConfig.table.rows;

  return (
    <div>
      <Row className="d-flex justify-content-between align-items-center">
        <Col>
          <h5>{tableConfig.table.title}</h5>
        </Col>
        <Col className="d-flex justify-content-end">
          {tableConfig.table.actions?.addRow && (
            <Button
              variant="outline-secondary"
              onClick={handleAddRow}
              className="mb-3"
            >
              Add Row
            </Button>
          )}
        </Col>
      </Row>
      <div style={{ overflowX: 'auto' }}>
        <Table striped bordered hover>
          <thead>
            <tr>
              {tableConfig.table.columns.map(col => (
                <th key={col.id}>
                  {col.label}
                  {col?.validation?.required && (
                    <span className="text-danger ms-1">*</span>
                  )}
                </th>
              ))}
              {tableConfig.table.actions?.removeRow && <th>Actions</th>}
            </tr>
          </thead>
          <tbody>
            {displayRows.map((row, index) => (
              <tr key={row.id || index}>
                {tableConfig.table.columns.map(col => (
                  <td
                    key={`${index}-${col.id}`}
                    style={{ minWidth: col.minWidth || '150px' }}
                  >
                    {col.type === 'dropdown' ? (
                      <Controller
                        name={`${tableConfig.id}[${index}].${col.id}`}
                        control={control}
                        defaultValue={row[col.id]}
                        rules={col.validation || {}}
                        render={({ field }) => (
                          <Form.Select
                            onChange={e => {
                              field.onChange(e.target.value);
                              handleRowChange(index, col.id, e.target.value);
                            }}
                            {...field}
                            isInvalid={
                              !!errors[tableConfig.id]?.[index]?.[col.id]
                            }
                          >
                            <option value="" disabled>
                              Select Parameter
                            </option>
                            {col.options.map(option => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </Form.Select>
                        )}
                      />
                    ) : col.type === 'number' ? (
                      <Controller
                        name={`${tableConfig.id}[${index}].${col.id}`}
                        control={control}
                        defaultValue={row[col.id]}
                        rules={col.validation || {}}
                        render={({ field }) => (
                          <Form.Control
                            {...field}
                            type="number"
                            isInvalid={
                              !!errors[tableConfig.id]?.[index]?.[col.id]
                            }
                            onChange={e => {
                              field.onChange(e.target.value);
                              handleRowChange(index, col.id, e.target.value);
                            }}
                          />
                        )}
                      />
                    ) : col.type === 'label' ? (
                      <Controller
                        name={`${tableConfig.id}[${index}].${col.id}`}
                        control={control}
                        defaultValue={row[col.id]}
                        render={() => <Form.Label>{row[col.id]}</Form.Label>}
                      />
                    ) : (
                      <Controller
                        name={`${tableConfig.id}[${index}].${col.id}`}
                        control={control}
                        defaultValue={row[col.id]}
                        rules={col.validation || {}}
                        render={({ field }) => (
                          <Form.Control
                            {...field}
                            type={col.type || 'text'}
                            isInvalid={
                              !!errors[tableConfig.id]?.[index]?.[col.id]
                            }
                            onChange={e => {
                              field.onChange(e.target.value);
                              handleRowChange(index, col.id, e.target.value);
                            }}
                          />
                        )}
                      />
                    )}
                    <Form.Control.Feedback type="invalid">
                      {errors[tableConfig.id]?.[index]?.[col.id]?.message}
                    </Form.Control.Feedback>
                  </td>
                ))}
                {tableConfig.table.actions?.removeRow && (
                  <td>
                    <button
                      key={index}
                      className="btn btn-outline-danger flex-shrink-0 btn-sm"
                      onClick={() => handleRemoveRow(index)}
                      title="Remove Row"
                      disabled={fields.length === 1}
                    >
                      <MdDelete style={{ fontSize: '20px' }} />
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
    </div>
  );
};
